import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import useAuthStore from '@/lib/stores/authStore';
import { loginSchema, type LoginFormData } from '@/lib/validations/auth';
import type { FirebaseAuthError } from '@/types';
import { ROUTES } from '@/types';

/**
 * Apple-inspired login form component
 * Handles user authentication with email and password
 */
export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { signIn, loading, error, clearError } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    watch,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur', // Validate when user leaves field
    reValidateMode: 'onChange', // Re-validate on change after first validation
  });

  const watchedFields = watch();

  // Track when fields are being actively edited
  const handleFieldFocus = (fieldName: 'email' | 'password') => {
    // Clear errors when user focuses on a field (starts typing)
    if (errors[fieldName]) {
      clearErrors(fieldName);
    }
    if (errors.root?.message) {
      clearErrors('root');
    }
    if (error) {
      clearError();
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearError(); // Clear store error
      await signIn(data.email, data.password);
      
      // Navigate directly to restaurants
      navigate(ROUTES.RESTAURANTS);
    } catch (error: unknown) {
      // Clear store error immediately since we're handling it in the form
      clearError();
      
      // Handle specific Firebase errors
      const firebaseError = error as FirebaseAuthError;
      if (firebaseError.code === 'auth/invalid-credential') {
        setError('root', { 
          message: 'Invalid email or password. Please check your credentials and try again.' 
        });
      } else if (firebaseError.code === 'auth/invalid-email') {
        setError('email', { 
          message: 'Invalid email address' 
        });
      } else if (firebaseError.code === 'auth/user-disabled') {
        setError('email', { 
          message: 'This account has been disabled' 
        });
      } else if (firebaseError.code === 'auth/too-many-requests') {
        setError('root', { 
          message: 'Too many failed attempts. Please try again later.' 
        });
      } else {
        setError('root', { 
          message: firebaseError.message || 'Sign in failed. Please try again.' 
        });
      }
    }
  };

  // Enable button when both fields have values and no current validation errors are showing
  const hasValidFormData = 
    watchedFields.email && 
    watchedFields.password && 
    !errors.email && 
    !errors.password;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Form Fields */}
      <div className="space-y-4">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Input
            {...register('email')}
            type="email"
            label="Email Address"
            placeholder="your@email.com"
            leftIcon={<Mail className="w-4 h-4" />}
            error={errors.email?.message}
            disabled={isSubmitting || loading}
            autoComplete="email"
            autoFocus
            onFocus={() => handleFieldFocus('email')}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Input
            {...register('password')}
            type="password"
            label="Password"
            placeholder="Enter your password"
            leftIcon={<Lock className="w-4 h-4" />}
            error={errors.password?.message}
            disabled={isSubmitting || loading}
            autoComplete="current-password"
            onFocus={() => handleFieldFocus('password')}
          />
        </motion.div>
      </div>
      {/* Error Display - Toast style overlay */}
      <AnimatePresence mode="wait">
        {errors.root?.message && (
          <motion.div
            key="error-message"
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-apple-lg"
          >
            <p className="text-sm font-medium text-red-800">
              {errors.root.message}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Forgot Password Link */}
      <div className="text-right">
        <Link
          to={ROUTES.FORGOT_PASSWORD}
          className="text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-200"
        >
          Forgot your password?
        </Link>
      </div>

      {/* Submit Button */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={isSubmitting || loading}
          rightIcon={!isSubmitting && !loading ? <ArrowRight className="w-4 h-4" /> : undefined}
          disabled={!hasValidFormData || isSubmitting}
        >
          {isSubmitting || loading ? 'Signing In...' : 'Sign In'}
        </Button>
      </motion.div>

      {/* Sign Up Link */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-center pt-4 border-t border-gray-100"
      >
        <p className="text-sm text-gray-600">
          Don't have an account?{' '}
          <Link
            to={ROUTES.SIGNUP}
            className="font-medium text-primary hover:text-primary/80 transition-colors duration-200"
          >
            Create one here
          </Link>
        </p>
      </motion.div>
    </form>
  );
};

export default LoginForm;